<script setup lang="ts">
import { computed } from 'vue'
import { Share } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useGoTo } from '@/composables/useGoTo'

// 可複用分享元件:LINE 專屬 + 通用分享(原生 sheet / 複製連結)。
// 放在場地詳情、搜尋卡、組合落地頁;只露分享/詢問,不露聯絡(守 demand-aggregator 護欄)。
const props = defineProps<{
  target: string          // 要分享的路徑,如 /space/123 或 /list/taipei/meeting-room
  source: string          // 歸因來源(ref),如 space_share / card_share / combo_share
  shareText?: string      // LINE/原生分享的文字;預設用通用句
  compact?: boolean       // true=icon-only(搜尋卡用);false=帶文字的 pill(詳情/組合頁用)
}>()

const { t } = useI18n()
const { buildShareUrl } = useGoTo()

const shareUrl = computed(() => buildShareUrl(props.target, props.source))
const message = computed(() => props.shareText || t('share.text'))

const baseBtn = 'inline-flex items-center justify-center cursor-pointer text-osn-dark-grey-00'
const lineClass = computed(() =>
  props.compact
    ? `${baseBtn} w-[34px] h-[34px] rounded-full border border-osn-grey-03 bg-white`
    : `${baseBtn} h-[40px] px-4 text-[15px] font-bold rounded-[50px] border border-osn-yellow-00 hover:bg-osn-yellow-00`
)
const genericClass = computed(() =>
  props.compact
    ? `${baseBtn} w-[34px] h-[34px] rounded-full border border-osn-grey-03 bg-white`
    : `${baseBtn} h-[40px] px-4 text-[15px] font-bold rounded-[50px] border border-osn-grey-02 hover:bg-osn-grey-04`
)

function track(method: string) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'share_click',
    method,
    share_source: props.source,
    share_target: props.target,
  })
}

function shareToLine() {
  track('line')
  const payload = `${message.value}\n${shareUrl.value}`
  const encoded = encodeURIComponent(payload)
  // 網頁分享:/R/msg/text/(不可用 /R/share?text=,會被導去 LINE 首頁)。
  const webUrl = `https://line.me/R/msg/text/?${encoded}`
  // 手機:line.me 通用連結本身會帶起 LINE app(未裝則自動走網頁),直接開即可。
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    window.open(webUrl, '_blank')
    return
  }
  // 桌面:優先用 line:// scheme 啟動桌面 app 分享;若未安裝(視窗沒被切走)則退回網頁。
  let switched = false
  const onBlur = () => { switched = true }
  window.addEventListener('blur', onBlur, { once: true })
  window.location.href = `line://msg/text/?${encoded}`
  window.setTimeout(() => {
    window.removeEventListener('blur', onBlur)
    if (!switched) window.open(webUrl, '_blank')
  }, 1200)
}

async function shareGeneric() {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    track('native')
    try {
      await navigator.share({ text: message.value, url: shareUrl.value })
    } catch {
      // 使用者取消原生分享 → 非錯誤,不處理
    }
    return
  }
  track('copy')
  try {
    await navigator.clipboard.writeText(shareUrl.value)
    ElMessage.success(t('share.copied'))
  } catch {
    ElMessage.error(t('share.copy_failed'))
  }
}
</script>

<template>
  <div class="inline-flex items-center gap-2" @click.stop>
    <button type="button" :class="lineClass" :aria-label="t('share.to_line')" @click.stop="shareToLine">
      <svg-icon name="line" :size="20" :class="compact ? '' : 'mr-1'" />
      <span v-if="!compact">{{ t('share.to_line') }}</span>
    </button>
    <button type="button" :class="genericClass" :aria-label="t('share.share')" @click.stop="shareGeneric">
      <el-icon :size="18" :class="compact ? '' : 'mr-1'"><Share /></el-icon>
      <span v-if="!compact">{{ t('share.share') }}</span>
    </button>
  </div>
</template>
